import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Copy, Share2, Gift, CheckCircle, QrCode, MessageCircle, 
  Twitter, Mail, ChevronLeft, Download,
  Fingerprint
} from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';

interface InviteFriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
}

// 🟢 SCOOP SIGNATURE THEMES (Brutalist & Streetwear aesthetics)
const QR_THEMES = [
  { id: 'noir', name: 'Noir', dotColor: '#ffffff', bgColor: '#000000', ringText: 'text-zinc-500', shadow: 'shadow-[0_0_40px_rgba(255,255,255,0.1)]' },
  { id: 'volt', name: 'Volt', dotColor: '#CEFF00', bgColor: '#000000', ringText: 'text-[#CEFF00]', shadow: 'shadow-[0_0_40px_rgba(206,255,0,0.2)]' },
  { id: 'crimson', name: 'Crimson', dotColor: '#FF0000', bgColor: '#000000', ringText: 'text-[#FF0000]', shadow: 'shadow-[0_0_40px_rgba(255,0,0,0.2)]' },
  { id: 'ghost', name: 'Ghost', dotColor: '#000000', bgColor: '#ffffff', ringText: 'text-zinc-400', shadow: 'shadow-[0_0_40px_rgba(255,255,255,0.3)]' }
];

export const InviteFriendsModal: React.FC<InviteFriendsModalProps> = ({
  isOpen,
  onClose,
  profile
}) => {
  const [showQR, setShowQR] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  
  const [activeTheme, setActiveTheme] = useState(QR_THEMES[0]); 
  const qrRef = useRef<HTMLDivElement>(null);
  const [qrCodeInstance, setQrCodeInstance] = useState<any>(null);

  // 🟢 CORRECT WEBSITE LINK LOGIC
  const referralCode = profile?.displayName 
    ? profile.displayName.replace(/\s/g, '').toUpperCase().substring(0, 8) + '24' 
    : 'SCOOPVIP';
  const profileLink = `https://hype.scoophype.co.za/profile/${profile?.uid || 'join'}`;

  // 🟢 DYNAMIC "S" LOGO GENERATOR
  // Creates a crisp, URL-safe SVG of an italicized "S" based on the current theme colors
  const getSLogo = (theme: typeof QR_THEMES[0]) => {
      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='${encodeURIComponent(theme.dotColor)}' rx='25'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='central' text-anchor='middle' font-family='Arial, sans-serif' font-size='75' font-weight='900' font-style='italic' fill='${encodeURIComponent(theme.bgColor)}'%3ES%3C/text%3E%3C/svg%3E`;
  };

  // 🟢 GENERATE THE CIRCULAR QR CODE
  useEffect(() => {
      if (showQR && qrRef.current) {
          const qrCode = new QRCodeStyling({
              width: 230,
              height: 230,
              data: profileLink,
              image: getSLogo(activeTheme), 
              dotsOptions: {
                  type: "rounded", // Smooth dots
                  color: activeTheme.dotColor
              },
              cornersSquareOptions: {
                  type: "extra-rounded",
                  color: activeTheme.dotColor
              },
              cornersDotOptions: {
                  type: "dot",
                  color: activeTheme.dotColor
              },
              backgroundOptions: {
                  color: "transparent", // Lets the coin background shine through
              },
              imageOptions: {
                  crossOrigin: "anonymous",
                  margin: 8,
                  imageSize: 0.35 // Perfect size for the "S"
              }
          });

          qrRef.current.innerHTML = '';
          qrCode.append(qrRef.current);
          setQrCodeInstance(qrCode);
      }
  }, [showQR, activeTheme, profileLink]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleDownloadQR = () => {
      if (qrCodeInstance) {
          qrCodeInstance.download({ name: `scoop-id-${referralCode.toLowerCase()}`, extension: "png" });
      }
  };

  const handleShare = (platform: string) => {
    const text = `Join my Scoop Network! Use code: ${referralCode}`;
    const url = encodeURIComponent(profileLink);
    const message = encodeURIComponent(text);
    
    let link = '';
    switch(platform) {
        case 'whatsapp': link = `https://wa.me/?text=${message}%20${url}`; break;
        case 'twitter': link = `https://twitter.com/intent/tweet?text=${message}&url=${url}`; break;
        case 'email': link = `mailto:?subject=${encodeURIComponent("Join my Scoop Network")}&body=${message}%20${url}`; break;
    }
    if (link) window.open(link, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[250] flex flex-col items-center justify-end lg:justify-center p-0 lg:p-6 bg-black/95 backdrop-blur-xl animate-in fade-in duration-300">
      
      <div className="w-full h-[95vh] lg:h-auto lg:max-h-[90vh] lg:max-w-2xl bg-black border-t lg:border border-zinc-800 flex flex-col relative animate-in slide-in-from-bottom lg:slide-in-from-bottom-0 duration-300 rounded-t-[3rem] lg:rounded-none overflow-hidden mt-auto lg:mt-0 shadow-2xl">
        
        {showQR ? (
            <>
              {/* Header */}
              <div className="p-8 border-b border-zinc-900 flex justify-between items-center bg-black sticky top-0 z-10">
                  <button onClick={() => setShowQR(false)} className="w-10 h-10 flex items-center justify-center bg-zinc-900 rounded-full text-white active:scale-90 transition-transform">
                      <ChevronLeft size={20} strokeWidth={3} />
                  </button>
                  <h2 className="text-xl font-black text-white uppercase tracking-[0.3em]">Identity Coin</h2>
                  <button onClick={handleDownloadQR} className="w-10 h-10 flex items-center justify-center bg-white rounded-full text-black active:scale-90 transition-transform">
                      <Download size={18} strokeWidth={3} />
                  </button>
              </div>

              <div className="flex-1 flex flex-col p-6 lg:p-10 items-center justify-center overflow-y-auto pb-safe">
                  
                  {/* 🟢 THE CIRCULAR "ID COIN" DESIGN */}
                  <div className="relative w-[320px] h-[320px] flex items-center justify-center mb-12">
                      
                      {/* Outer Rotating Text Ring */}
                      <svg className="absolute inset-0 w-full h-full animate-[spin_15s_linear_infinite] pointer-events-none" viewBox="0 0 100 100">
                          <path id="circlePath" d="M 50, 50 m -45, 0 a 45,45 0 1,1 90,0 a 45,45 0 1,1 -90,0" fill="transparent" />
                          <text className={`text-[6.5px] font-black uppercase tracking-[0.2em] fill-current ${activeTheme.ringText}`}>
                              <textPath href="#circlePath" startOffset="0%">
                                  SCOOP NETWORK • VERIFIED IDENTITY • SECURE PROTOCOL • 
                              </textPath>
                          </text>
                      </svg>

                      {/* Inner Solid Coin */}
                      <div 
                          className={`w-[260px] h-[260px] rounded-full flex items-center justify-center border-[3px] border-zinc-800 ${activeTheme.shadow} transition-all duration-500`}
                          style={{ backgroundColor: activeTheme.bgColor }}
                      >
                          {/* The QR Code gets injected directly into this container */}
                          <div ref={qrRef} className="flex items-center justify-center pointer-events-none" />
                      </div>
                  </div>

                  {/* Username Display */}
                  <div className="w-full text-center mb-10">
                      <div className="flex items-center justify-center gap-2 text-zinc-500 font-mono text-[10px] uppercase tracking-widest mb-3">
                          <Fingerprint size={12} /> Biometric Match
                      </div>
                      <h2 className="text-white font-black text-4xl uppercase italic tracking-tighter leading-none mb-4">
                          {profile?.displayName?.split(' ')[0] || 'Member'}
                      </h2>
                  </div>

                  {/* Theme Selectors */}
                  <div className="flex gap-4">
                      {QR_THEMES.map(t => (
                          <button 
                            key={t.id} 
                            onClick={() => setActiveTheme(t)}
                            className={`w-12 h-12 rounded-full border-2 transition-all flex items-center justify-center ${activeTheme.id === t.id ? 'border-white scale-110' : 'border-zinc-800 opacity-50 hover:opacity-100'}`}
                            style={{ backgroundColor: t.bgColor }}
                          >
                              {/* Show dot color inside the button as a preview */}
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: t.dotColor }} />
                          </button>
                      ))}
                  </div>
              </div>
            </>
        ) : (
            <>
              {/* Standard View (Links & Codes) */}
              <div className="p-8 border-b border-zinc-900 flex justify-between items-center bg-black">
                <h2 className="text-2xl lg:text-3xl font-black text-white uppercase italic tracking-tighter">Share Profile</h2>
                <button onClick={onClose} className="w-10 h-10 flex items-center justify-center bg-zinc-900 rounded-full text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 space-y-10 pb-safe">
                  
                  <div className="bg-zinc-900/50 p-8 rounded-[2rem] lg:rounded-none border border-zinc-800 relative overflow-hidden group">
                      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, #fff 2px, #fff 4px)' }}></div>
                      <div className="relative z-10">
                          <Gift size={32} className="text-white mb-4" />
                          <h3 className="text-2xl font-black text-white uppercase italic mb-2">User Rewards</h3>
                          <p className="text-zinc-400 font-mono text-xs uppercase tracking-widest leading-relaxed">
                              Link a new user to receive 100 CRD upon their first asset acquisition.
                          </p>
                      </div>
                  </div>

                  <div className="space-y-6">
                      <button onClick={handleCopyLink} className="w-full bg-zinc-950 border border-zinc-800 p-6 rounded-2xl lg:rounded-none flex items-center justify-between group active:scale-[0.98] transition-transform">
                          <span className="text-white font-mono text-xs truncate max-w-[220px]">{profileLink}</span>
                          {copiedLink ? <CheckCircle size={20} className="text-green-500 shrink-0"/> : <Copy size={20} className="text-zinc-600 group-hover:text-white shrink-0"/>}
                      </button>

                      <div className="flex gap-4">
                          <button onClick={handleCopyCode} className="flex-1 bg-zinc-950 border border-zinc-800 p-6 rounded-2xl lg:rounded-none flex items-center justify-between group active:scale-[0.98] transition-transform">
                              <span className="text-white font-black text-xl tracking-[0.3em] uppercase truncate">{referralCode}</span>
                              {copiedCode ? <CheckCircle size={20} className="text-green-500 shrink-0"/> : <Copy size={20} className="text-zinc-600 group-hover:text-white shrink-0"/>}
                          </button>

                          <button onClick={() => setShowQR(true)} className="w-20 bg-white text-black rounded-2xl lg:rounded-none flex items-center justify-center hover:bg-zinc-200 transition-colors shadow-xl shrink-0 active:scale-95">
                              <QrCode size={24} />
                          </button>
                      </div>
                  </div>

                  <div>
                       <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3 block pl-2 lg:pl-0">Broadcast Via</label>
                       <div className="grid grid-cols-4 gap-3">
                           <button onClick={() => handleShare('whatsapp')} className="bg-zinc-950 border border-zinc-800 py-5 rounded-2xl lg:rounded-none flex items-center justify-center text-zinc-400 hover:text-white hover:border-white transition-all active:scale-95"><MessageCircle size={24} /></button>
                           <button onClick={() => handleShare('twitter')} className="bg-zinc-950 border border-zinc-800 py-5 rounded-2xl lg:rounded-none flex items-center justify-center text-zinc-400 hover:text-white hover:border-white transition-all active:scale-95"><Twitter size={24} /></button>
                           <button onClick={() => handleShare('email')} className="bg-zinc-950 border border-zinc-800 py-5 rounded-2xl lg:rounded-none flex items-center justify-center text-zinc-400 hover:text-white hover:border-white transition-all active:scale-95"><Mail size={24} /></button>
                           <button onClick={() => handleShare('twitter')} className="bg-zinc-950 border border-zinc-800 py-5 rounded-2xl lg:rounded-none flex items-center justify-center text-zinc-400 hover:text-white hover:border-white transition-all active:scale-95"><Share2 size={24} /></button>
                       </div>
                  </div>
              </div>
            </>
        )}
      </div>
    </div>
  );
};